import Invoice from "@/types/Invoice.interface"
import { InvoiceParams } from '@/types/PaginationParams.interface'
import Message from "@/types/Message.interface"
import Pagination from "@/types/Pagination.interface"
import Person from "@/types/Person.interface"
import { mapState } from "vuex"

interface State {
    paymentsInvoices: Invoice[]
    paymentsInvoicesCurrentPage: number
    paymentsInvoicesLastPage: number
    paymentsInvoicesNextPage: number
    paymentsInvoicesPaginating: boolean
    paymentsInvoicesParams: InvoiceParams
    paymentsInvoicesParamsDefault: InvoiceParams
    paymentsSelectedInvoice: Invoice
    paymentsSelectedInvoicePaginating: boolean
    paymentsSelectedInvoiceMessages: Message[]
    paymentsSelectedInvoiceSelectedMessage: Message
}

const defaults = () => {
    return {
        paymentsInvoices: [],
        paymentsInvoicesCurrentPage: null,
        paymentsInvoicesLastPage: null,
        paymentsInvoicesNextPage: null,
        paymentsInvoicesPaginating: false,
        paymentsInvoicesParams: { page: 1, filter: 'open' },
        paymentsInvoicesParamsDefault: { page: 1, filter: 'open' },
        paymentsSelectedInvoice: null,
        paymentsSelectedInvoicePaginating: false,
        paymentsSelectedInvoiceMessages: [],
        paymentsSelectedInvoiceSelectedMessage: null
    }
}

export default {
    state: defaults(),
    mutations: {
        paymentsInvoicesParamsSet (state: State, params: InvoiceParams): void {
            state.paymentsInvoicesParams = params
        },
        paymentsInvoicesPush (state: State, invoices: Invoice[]): void {
            state.paymentsInvoices = window.removeDuplicatesById(state.paymentsInvoices, invoices)

            //  Get the currently selected invoice
            const selectedInvoice = state.paymentsSelectedInvoice

            //  If the invoice does NOT exist, do nothing
            if (!selectedInvoice) return

            //  Else, find if they are in the updated invoices list
            const updatedInvoice = invoices.find((x: Invoice) => x.id == selectedInvoice.id)

            //  If not found, then do nothing
            if (!updatedInvoice) return

            //  Else, updated the selected invoice
            state.paymentsSelectedInvoice = updatedInvoice
        },
        paymentsInvoicesRemove (state: State, ids: number[]): void {
            //  Get our current lists of invoices
            const invoices = state.paymentsInvoices

            //  If no invoices, do nothing
            if (!invoices) return

            //  Else, filter out our removed ids
            const filtered = invoices.filter((x: Invoice) => !ids.includes(x.id))

            //  Replace with the filtered list
            state.paymentsInvoices = filtered

            //  Get the currently selected invoice
            const selectedInvoice = state.paymentsSelectedInvoice

            //  If the invoice does NOT exist, then do nothing
            if (!selectedInvoice) return

            //  Else, check if they are in our array
            const deleted = ids.find((x: number) => x === selectedInvoice.id)

            //  If not found, do nothing
            if (!deleted) return

            //  Else, set the selected invoice to null since it is now deleted
            state.paymentsSelectedInvoice = null
        },
        paymentsInvoicesSync (state: State, updates: Invoice[]): void {
            //  Get our list of invoices
            const invoices = state.paymentsInvoices

            //  If no invoices, do nothing
            if (!invoices) return

            //  Grab our invoice filter params
            const params = state.paymentsInvoicesParams

            //  Apply our inline filtering
            const filtered = updates.appSyncFilter(params, 'payments')

            //  Find the items we will have to remove because of the filter
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Update our list with the filtered array
            state.paymentsInvoices = window.removeDuplicatesById(state.paymentsInvoices, filtered, remove)

            //  Check if we have a selected invoice
            const selected = state.paymentsSelectedInvoice

            //  If it doesn't exist, do nothing
            if (!selected) return

            //  Else, check if our filtered contains this ids
            const exists = updates.find((x: Invoice) => x.id == selected.id)

            //  If it does NOT contain the id, do nothing
            if (!exists) return

            //  Else, update our selected invoice
            state.paymentsSelectedInvoice = exists
        },
        paymentsInvoicesSet (state: State, invoices: Invoice[]): void {
            state.paymentsInvoices = invoices
        },
        paymentsPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset our invoices list
            if (page.current_page === 1) state.paymentsInvoices = []

            state.paymentsInvoicesCurrentPage = page.current_page
            state.paymentsInvoicesLastPage = page.last_page
            state.paymentsInvoicesNextPage = page.current_page + 1
        },
        paymentsInvoicesPaginatingSet (state: State, bool: boolean): void {
            state.paymentsInvoicesPaginating = bool
        },
        paymentsInvoicesPersonsSync (state: State, person: Person): void {
            //  Grab our invoices
            const invoices = state.paymentsInvoices

            //  Find our invoices belonging to this person
            const updated = invoices.filter((x: Invoice) => x.person_id === person.id)

            //  If none, do nothing
            if (!updated) return

            //  Else, make sure we update the persons on thse invoices with this new person
            updated.forEach((x: Invoice) => x.person = person)

            //  Make sure we update our state
            state.paymentsInvoices = window.removeDuplicatesById(state.paymentsInvoices, updated)

            //  Check if we have a selected invoice
            const selected = state.paymentsSelectedInvoice

            //  If we do not then do nothing
            if (!selected) return

            //  Else, check if the person matches
            const match = selected.person_id === person.id

            //  If it doesn't match, we are done
            if (!match) return

            //  Else, update the person
            selected.person = person

            //  Update our store
            state.paymentsSelectedInvoice = selected
        },
        paymentsSelectedInvoiceSet (state: State, invoice: Invoice): void {
            state.paymentsSelectedInvoice = invoice
        },
        paymentsSelectedInvoicePaginatingSet (state: State, bool: boolean): void {
            state.paymentsSelectedInvoicePaginating = bool
        },
        paymentsSelectedInvoiceMessagesSet (state: State, messages: Message[]): void {
            state.paymentsSelectedInvoiceMessages = messages
        },
        paymentsSelectedInvoiceSelectedMessageSet (state: State, messages: Message[]): void {
            //  If this is an empty array, do nothing
            if (!messages || !messages.length) state.paymentsSelectedInvoiceSelectedMessage = null
            //  else, set this to the first message
            else state.paymentsSelectedInvoiceSelectedMessage = messages[messages.length - 1]
        },
        paymentsSelectedInvoiceMessagesRemove (state: State, ids: number[]): void {
            //  Grab our selected invoice
            const invoice = state.paymentsSelectedInvoice

            //  If we don't have a selected invoice, we shouldn't do anything
            if (!invoice) return

            //  Grab our current list of messages
            const messages = state.paymentsSelectedInvoiceMessages

            //  If no selected messages, do nothing
            if (!messages) return

            //  Filter out messages so we remove any "ids" from our state
            const filtered = messages.filter((x: Message) => !ids.includes(x.id))

            //  Replace our list with the filtered
            state.paymentsSelectedInvoiceMessages = filtered

            //  Check if we have a selected message
            const selected = state.paymentsSelectedInvoiceSelectedMessage

            //  If we don't, we are done so just return
            if (!selected) return

            //  Else, check if we removed this message
            const deleted = ids.find((x: number) => x == selected.id)

            //  If we did not, then do nothing
            if (!deleted) return

            //  Else, set our message to the latest messages we have in the list
            state.paymentsSelectedInvoiceSelectedMessage = filtered[filtered.length - 1]
        },
        paymentsSelectedInvoiceMessagesSync (state: State, messages: Message[]): void {
            //  Grab our selected invoice
            const invoice = state.paymentsSelectedInvoice

            //  If we don't have a selected invoice, we shouldn't do anything
            if (!invoice) return

            //  Else, filter out our messages to only contain messages with this invoice id
            const filtered = messages.filter((x: Message) => x.invoice_id === invoice.id)

            //  Update our list
            state.paymentsSelectedInvoiceMessages = window.removeDuplicatesById(state.paymentsSelectedInvoiceMessages, filtered)

            //  Check if we have a selected message
            const selected = state.paymentsSelectedInvoiceSelectedMessage

            //  If we don't, we are done so just return
            if (!selected) return

            //  Else, check if our filtered contains this id
            const exists = filtered.find((x: Message) => x.id === selected.id)

            //  If we don't have it, do nothing
            if (!exists) return

            //  Else, replace it
            state.paymentsSelectedInvoiceSelectedMessage = exists
        },
        paymentsReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        paymentsInvoicesPaginate ({ commit, getters }): Promise<Pagination> {
            //  Select our pagination params
            const p = getters.paymentsInvoicesParams

            //  Start the paginating tracker
            commit('paymentsInvoicesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/invoices?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination params
                commit('paymentsPaginationPush', response.data)

                //  Update the payments invoices list
                commit('paymentsInvoicesPush', response.data.data)

                //  Stop the paginating tracker
                commit('paymentsInvoicesPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(e => console.error("PaymentsInvoicesPaginate ERROR", e))
        },
        paymentsSelectedInvoiceGet ({ commit, getters }, invoiceId: number): Promise<Invoice> {
            //  Grab the global invoices list
            const invoices = getters.invoices

            //  Check if this invoice already exists within
            const existingInvoice = invoices.find((x: Invoice) => x.id == invoiceId)

            //  If there IS an existing invoice, commit that and proceed
            if (existingInvoice) return commit('paymentsSelectedInvoiceSet', existingInvoice)

            //  Start our pagination tracker
            commit('paymentsSelectedInvoicePaginatingSet', true)

            //  Else, assemble the API endpoint
            const url = `${getters.api}/invoices/${invoiceId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the selected invoice
                commit('paymentsSelectedInvoiceSet', response.data)

                //  Push into our global dataset
                commit('invoicesPush', [response.data])

                //  Stop paginating
                commit('paymentsSelectedInvoicePaginatingSet', false)

                //  Return our invoice
                return response.data
            }).catch(() => null)
        },
        paymentsSelectedInvoiceMessagesGet ({ commit, getters }, invoiceId: number): Promise<Message[]> {
            //  Start paginating
            commit('paymentsSelectedInvoicePaginatingSet', true)
            
            //  Assemble our API endpoint
            const url = `${getters.api}/invoices/messages/${invoiceId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the payments selected invoice messages
                commit('paymentsSelectedInvoiceMessagesSet', response.data)

                //  Set our selected invoice selected messages
                commit('paymentsSelectedInvoiceSelectedMessageSet', response.data)

                //  Stop paginating
                commit('paymentsSelectedInvoicePaginatingSet', false)

                //  Return the messages
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        paymentsInvoices (state: State): Invoice[] {
            return state.paymentsInvoices
        },
        paymentsInvoicesCurrentPage (state: State): number {
            return state.paymentsInvoicesCurrentPage
        },
        paymentsInvoicesLastPage (state: State): number {
            return state.paymentsInvoicesLastPage
        },
        paymentsInvoicesNextPage (state: State): number {
            return state.paymentsInvoicesNextPage
        },
        paymentsInvoicesPaginating (state: State): boolean {
            return state.paymentsInvoicesPaginating
        },
        paymentsInvoicesParams (state: State): InvoiceParams {
            return state.paymentsInvoicesParams
        },
        paymentsInvoicesParamsDefault (state: State): InvoiceParams {
            return state.paymentsInvoicesParamsDefault
        },
        paymentsSelectedInvoice (state: State): Invoice {
            return state.paymentsSelectedInvoice
        },
        paymentsSelectedInvoicePaginating (state: State): boolean {
            return state.paymentsSelectedInvoicePaginating
        },
        paymentsSelectedInvoiceMessages (state: State): Message[] {
            return state.paymentsSelectedInvoiceMessages
        },
        paymentsSelectedInvoiceSelectedMessage (state: State): Message {
            return state.paymentsSelectedInvoiceSelectedMessage
        }
    }
}
